import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { DiagnosticResult } from './diagnosticResults';
import { ConsultationFinding } from './consultationFindings';
import { ConsultationInvestigation } from './consultationInvestigations';
import { ConsultationRecommendation } from './consultationRecommendations';
import { IServiceInfoByLocation } from './services';

export interface SalesOrderInfo {
  sales_order: SalesOrder;
  customer_person: Person;
  sales_order_appointment: SalesOrderAppointment;
  sales_order_item: SalesOrderItem;
  product: Product;
  product_group: ProductGroup;
  service: Service;
  service_service_group: ServiceServiceGroup;
  work_order?: WorkOrder;
  work_order_service_diagnostic?: WorkOrderServiceDiagnostic;
  work_order_service_consultation?: WorkOrderServiceConsultation;
  diagnostic_results?: DiagnosticResult[];
  employeePerson?: Person;
  validationByPerson?: Person;
  gender: any;
  nationality?: Nationality;
  consultation_findings?: ConsultationFinding;
  consultation_investigations?: ConsultationInvestigation;
  consultation_recommendations?: ConsultationRecommendation;
  amount_paid?: number;
  vat?: number;
  // checks where the service can be executed (internal or external laboratory)
  serviceAdditionalInfo?: IServiceInfoByLocation;
    translated_service_name?: string;
}

export interface Person {
  person_id: number;
  person_external_id: number;
  person_tittle: number;
  person_firstname: string;
  person_lastname: string;
  person_gender_id: number;
  person_birthdate?: Date;
  person_email: string;
  person_mobile_phone: string;
  person_landline_phone: string;
  person_nationality_id: number;
  person_category_id: number;
  person_account_number: string | undefined;
  weight: number;
  height: number;
  blood_type: string;
  relation_status: string;
}
interface SalesOrder {
  sales_order_id: number;
  sales_order_external_id: number;
  datetime_create: Date;
  customer_person_id: number;
  business_person_id: number;
  items_subtotal?: number;
  amount?: number;
  vat?: number;
  currency?: string;
  payment_method_id?: number;
  person_id_create?: number;
  ta_id?: number;
  transaction_id?: number;
  payment_method?: string;
  paid?: string;
}
interface SalesOrderAppointment {
  sales_order_appointment_id: number;
  sales_order_id: number;
  person_assign_consutation_name: string;
  start?: Date;
  end_time?: Date;
  business_partner_timezone_id?: number;
  record_state?: string;
  valid_from: Date;
  invalid_from: Date;
  status?: string;
  sales_order_appointment_status: string;
}
interface SalesOrderItem {
  sales_order_id: number;
  product_id: number;
  unit_of_measure_id: any;
  quantity: any;
  currency: string;
  amount: any;
  sales_order_item_id: number;
  external_sales_order_item_id: number;
  datetime_created: any;
  with_chatting: any;
  datetime_cancel: any;
  person_cancel: any;
  record_state: string;
}
interface Product {
  product_id: number;
  external_product_id: number;
  product_name: string;
  product_long_description: string;
}
interface ProductGroup {
  product_group: string;
  product_group_id: number;
  product_group_name: string;
}
interface Service {
  service_id: number;
  service_name: string;
  service_group_id: number;
}
interface ServiceServiceGroup {
  service_id: number;
  service_group_id: number;
}
interface WorkOrder {
  work_order_id: number;
  work_order_status_id: number;
}
interface WorkOrderServiceDiagnostic {
  work_order_id: number;
  service_id: number;
  sample_taken_id: number;
  diagnostic_business_partner_id: number;
  diagnostic_validation_employee_id: number;
  check_in_time?: string;
  outside_laboratory?: number;
  result_delivery_time?: string;
}
interface WorkOrderServiceConsultation {
  work_order_id: number;
  service_id: number;
}
interface Nationality {
  nationality_name: string;
}
 export interface ServiceTranslation {
  service_id: string;
  translations: {
    language: string;
    service_name: string;
  }[];
}
export interface SalesOrdersState {
  status: string;
  updateSalesOrderAppointmentStatus: string;
  salesOrderInfos: SalesOrderInfo[];
  orderIdsData: any;
  servicesTranslations: ServiceTranslation[];
}


const initialState: SalesOrdersState = {
  status: 'idle',
  updateSalesOrderAppointmentStatus: 'idle',
  salesOrderInfos: [],
  orderIdsData: {},
  servicesTranslations:[]
};

export const fetchSalesOrders = createAsyncThunk(
  'salesOrders/fetchAll',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const salesOrdersResponse: any = await client.get(
        `/healthpoint/sales-orders/${state?.auth?.accountInfo?.account_number}`
      );
      return salesOrdersResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);
export const fetchServicesTranslations = createAsyncThunk(
  'servicesTranslations/fetchAll',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      // const state: any = getState();
      const servicesTranslationsResponse: any = await client.get(
        `/healthpoint/object-translations/service`
      );
      return servicesTranslationsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

interface UpdateSalesOrderAppointmentStatusParams {
  salesOrderAppointmentId: string;
  status: string;
}
export const updateSalesOrderAppointmentStatus = createAsyncThunk(
  'salesOrderAppointment/updateStatus',
  async (
    updateSalesOrderAppointmentStatusParams: UpdateSalesOrderAppointmentStatusParams,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updateSalesOrderAppointmentStatusResponse: any = await client.put(
        `/healthpoint/sales-order-appointment/status/${updateSalesOrderAppointmentStatusParams.salesOrderAppointmentId}`,
        {
          status: updateSalesOrderAppointmentStatusParams.status,
        }
      );
      return updateSalesOrderAppointmentStatusResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const getOrderIds = createAsyncThunk(
  'salesOrders/getOrderIds',
  async (params: any, thunkApi) => {
    try {
      const response: any = await client.get(
        `/healthpoint/order-ids/${params.taId}/${params.productOrderId}/${params.productOrderItemId}`
      );
      return response;
    } catch (err: any) {
      return thunkApi.rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const salesOrdersSlice = createSlice({
  name: 'salesOrders',
  initialState,
  reducers: {
    resetSalesOrders: (_) => {
      return initialState;
    },
    resetUpdateSalesOrderAppointmentStatus: (state) => {
      return {
        ...state,
        updateSalesOrderAppointmentStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesOrders.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchSalesOrders.fulfilled, (state, action) => {
        if (action.payload.data.salesOrders?.length > 0) {
          state.salesOrderInfos = action.payload.data.salesOrders.map((s: any) => {
            return {
              ...s.sales_order_info,
              work_order: s.sales_order_info.work_order.work_order_id
                ? s.sales_order_info.work_order
                : null,
              work_order_service_diagnostic: s.sales_order_info.work_order_service_diagnostic
                .work_order_id
                ? s.sales_order_info.work_order_service_diagnostic
                : null,
              work_order_service_consultation: s.sales_order_info.work_order_service_consultation
                .work_order_id
                ? s.sales_order_info.work_order_service_consultation
                : null,
            };
          });
        } else {
          state.salesOrderInfos = [];
        }
        // state.salesOrderInfos = action.payload.data.salesOrders;
        state.status = 'fulfilled';
      })
      .addCase(fetchSalesOrders.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(updateSalesOrderAppointmentStatus.pending, (state) => {
        state.updateSalesOrderAppointmentStatus = 'pending';
      })
      .addCase(updateSalesOrderAppointmentStatus.fulfilled, (state, action) => {
        state.updateSalesOrderAppointmentStatus = 'fulfilled';
        toast.success('Successfully finalized the consultation.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(updateSalesOrderAppointmentStatus.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.updateSalesOrderAppointmentStatus = 'rejected';
      })
      .addCase(fetchServicesTranslations.pending, (state) => {
      state.status = 'pending';
    })
    .addCase(fetchServicesTranslations.fulfilled, (state, action) => {
      state.servicesTranslations = action.payload.data.servicesTranslations ?? [];
      state.status = 'fulfilled';
    })
    
    .addCase(fetchServicesTranslations.rejected, (state, action: any) => {
      toast.error(action?.payload?.error ?? 'Failed to fetch service translations.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      state.status = 'rejected';
    })
      .addCase(getOrderIds.pending, (state, action) => {
        state.status = 'pending';
      })
      .addCase(getOrderIds.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.orderIdsData = action.payload.data;
      })
      .addCase(getOrderIds.rejected, (state, action) => {
        state.status = 'rejected';
      });
  },
});

export const { resetSalesOrders, resetUpdateSalesOrderAppointmentStatus } =
  salesOrdersSlice.actions;

export default salesOrdersSlice.reducer;

export const selectSalesOrders = (state: RootState) => state.salesOrders.salesOrderInfos;
export const selectSalesOrdersStatus = (state: RootState) => state.salesOrders.status;
export const selectSalesOrderAppointmentUpdateStatus = (state: RootState) =>
  state.salesOrders.updateSalesOrderAppointmentStatus;
export const selectOrderIdsData = (state: RootState) => state.salesOrders.orderIdsData;
export const selectServicesTranslations = (state: RootState) =>
  state.salesOrders.servicesTranslations;

